import { Component, inject, Injector, Input, OnDestroy, OnInit, runInInjectionContext, StaticProvider } from '@angular/core';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DacService } from '../../dac.service';
import { CompanyService } from '@/pages/settings/company/company.service';
import { DacDetails, DacDocsFormResponse, DocType, DynaDocDetails, User } from '@/types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { DacWorkflowBlocksService } from '../../dac-workflow-blocks.service';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { Router } from '@angular/router';
import { DacTransformationService } from '../../dac-transformation.service';
import { getDacInspectFormObject } from '../utils/dac-inspect-form-object';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { CommonModule } from '@angular/common';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacTransactionalFormAssignmentService } from '../../dac-transactional-form.service';
import { DacSubmissionPiecesService } from '../../dac-submission-pieces.service';
import { DocSubmissionService } from '@/pages/settings/doc-submission/doc-submission.service';
import { dacDocSubmissionStoreFactory } from '@/store/dac-doc-submission/dac-doc-submission-state.store';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { getDACPVItems } from '../../utils/dac-pv-items';
import { DacMetaDataBlocksService } from '../../dac-metadata-blocks.service';

@Component({
  selector: 'sm-dac-inspect-content-block',
  standalone: true,
  templateUrl: './content-block.component.html',
  styleUrls: ['./content-block.component.scss'],
  imports: [CommonModule, FormBuilderComponent, StatusComponent, BlockBuilderComponent, SpinnerComponent],
  providers: [{ provide: 'MultiFormAccordionServiceForTransactionalForm', useClass: MultiFormAccordionService }],
})
export class DacInspectContentBlockComponent implements OnInit, OnDestroy {
  private readonly dacRepository = inject(DacRepository);
  private readonly dacService = inject(DacService);
  private readonly dacMetadataBlocksService = inject(DacMetaDataBlocksService);
  private readonly committeeService = inject(CommitteeService);
  private readonly fileService = inject(FileService);

  // transactional form deps for multiformaccordion
  multiFormAccordionForTransactionalForm = inject<MultiFormAccordionService>('MultiFormAccordionServiceForTransactionalForm' as any);
  dacTransactionalFormAssignmentService = new DacTransactionalFormAssignmentService(this.multiFormAccordionForTransactionalForm);
  // submission pieces deps for multiformaccordion
  multiFormAccordionsForSubmissionPieces: BehaviorSubject<MultiFormAccordionService[]> = new BehaviorSubject<MultiFormAccordionService[]>([]);
  dacSubmissionPiecesServices: BehaviorSubject<DacSubmissionPiecesService[]> = new BehaviorSubject<DacSubmissionPiecesService[]>([]);

  private readonly dacWorkflowBlocksService = inject(DacWorkflowBlocksService);
  private readonly dacTransformationService = inject(DacTransformationService);
  private readonly companyService = inject(CompanyService);
  private readonly docSubmissionService = inject(DocSubmissionService);
  private readonly injector = inject(Injector);

  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);
  private _id?: number;
  @Input() set id(value: number | undefined) {
    this._id = value;
    this.tryLoadData();
  }
  get id(): number | undefined {
    return this._id;
  }

  private _version?: number;
  @Input() set version(value: number | undefined) {
    this._version = value;
    this.tryLoadData();
  }
  get version(): number | undefined {
    return this._version;
  }

  @Input() setBreadcrumbElement = (index: number, attribute: 'label' | 'routerLink', value: string | string[]) => {
    console.warn('this method is not implemented', index, attribute, value);
  };

  ngOnInit(): void {
    this.tryLoadData();
  }

  ngOnDestroy(): void {
    this.dacRepository.reset();
    this.multiFormAccordionForTransactionalForm.reset(0);
  }

  loading$ = new BehaviorSubject<boolean>(false);
  formObject: DynamicForm = getFormBuilderObjectFactory();
  documents: DynaDocDetails[] = [];
  validationValues: Record<number, DacDocsFormResponse[]> = {};
  currencyOptions: { name: string; code: number }[] = [];
  dacDetails: DacDetails | null = null;
  controlBlocks: DynamicBlocks[] = [];
  metadataBlocks: DynamicBlocks[] = [];
  isMetadataExisting = new BehaviorSubject<boolean>(false);
  docTypes = new BehaviorSubject<DocType[]>([]);
  cpmpMembers = new BehaviorSubject<User[]>([]);
  nonBlacklistedCompanies = new BehaviorSubject<SelectOption[]>([]);

  pdfBase64 = '';
  pdfFilename = '';
  pdfUrl = new BehaviorSubject<string>('');

  private tryLoadData(): void {
    // Only load if both id and version are defined
    if (this._id == null || this._version == null) {
      // Optionally reset state if needed
      this.loading$.next(false);
      return;
    }

    this.loadData(this._id, this._version);
  }

  private loadData(id: number, version?: number): void {
    this.loading$.next(true);

    forkJoin([
      this.dacService.getDacById(id, version),
      this.dacService.getDacValidationData(id, version),
      this.dacService.getDacDetails(id),
      this.dacService.getMonnaies(),
      this.companyService.getNonBlacklistedCompanies(),
      this.docSubmissionService.getAllFolderTypes(),
    ]).subscribe({
      next: ([dacInfo, validationData, dacDetails, monnaies, nonBlacklistedCompanies, docTypes]) => {
        this.dacSubmissionPiecesServices.next([]);
        this.docTypes.next(docTypes);
        const dacData = dacDetails.dacDocs;
        const pvItems = getDACPVItems(dacDetails.dacDocs);
        const metadataBlocksResult = this.dacMetadataBlocksService.getMetadataBlocks({ pvItems, dacOpinions: dacDetails.avis });
        this.metadataBlocks = metadataBlocksResult.blocks;
        this.isMetadataExisting.next(!metadataBlocksResult.isEmpty);

        this.controlBlocks = this.dacWorkflowBlocksService.getInspectDacBlocks({
          refreshCount: dacData.nbrActualisation || 0,
          status: dacData.status,
          accessHistory: () => this.router.navigate(['/dac-task-history', dacDetails.processInstanceId]),
          downloadSignedAAOModel: dacData.dacModels?.[0]?.aaoModel
            ? () => this.downloadSignedAAOModel(dacData.reference, dacData.dacModels?.[0]?.aaoModel)
            : undefined,
          downloadSignedDACModel: dacData.dacModels?.[0]?.dacModel
            ? () => this.downloadSignedDACModel(dacData.reference, dacData.dacModels?.[0]?.dacModel)
            : undefined,
        });
        runInInjectionContext(this.injector, () => {
          const dynamicSubmissionProviders: StaticProvider[] = docTypes.map(item => ({
            provide: `MultiFormAccordionServiceForSubmissionPieces_${item.libelle}`,
            useClass: MultiFormAccordionService,
            deps: [],
          }));

          const injector = Injector.create({
            providers: dynamicSubmissionProviders,
            parent: this.injector,
          });

          this.multiFormAccordionsForSubmissionPieces.next(
            docTypes.map(docType => injector.get(`MultiFormAccordionServiceForSubmissionPieces_${docType.libelle}`))
          );

          this.multiFormAccordionsForSubmissionPieces.getValue().forEach(multiFormAccordionForSubmissionPieces => {
            multiFormAccordionForSubmissionPieces.formFactory = (id: string) => dacDocSubmissionStoreFactory(id);
            multiFormAccordionForSubmissionPieces.reset(0);
            const service = new DacSubmissionPiecesService(multiFormAccordionForSubmissionPieces);
            this.dacSubmissionPiecesServices.next([...this.dacSubmissionPiecesServices.getValue(), service]);
          });
        });
        this.nonBlacklistedCompanies.next(
          nonBlacklistedCompanies.map(company => ({ name: `${company.nom}-${company.nif}-${company.address.pays.code}`, code: company.id }))
        );

        this.committeeService.getCpmpMembers(dacDetails.dacDocs.dacId).subscribe(members => {
          this.cpmpMembers.next(members);
        });

        this.dacDetails = dacDetails;
        this.currencyOptions = (monnaies || []).map((m: any) => ({ name: m.description, code: m.id }));

        this.setBreadcrumbElement(0, 'label', dacDetails.dacDocs.aoReference);
        this.setBreadcrumbElement(1, 'routerLink', ['/edit-call-for-tender', dacDetails.dacDocs.aoId.toString()]);
        this.setBreadcrumbElement(1, 'label', dacInfo.reference);

        this.dacRepository.set('dacInfo', {
          ...dacInfo,
          aoReference: dacDetails.dacDocs.aoReference,
          dacPrix: dacInfo.dacPrix.map(dacPrix => ({
            ...dacPrix,
            monnaieId: dacPrix.monnaie.id,
            prix: dacPrix.prix,
          })),
        });
        this.dacRepository.set('responsableSelected', { code: dacInfo.responsableDacId });
        this.dacTransactionalFormAssignmentService.setTransactionalFormDtos(dacDetails.dacDocs.dynaForms);
        this.dacSubmissionPiecesServices.getValue().forEach((service, index) => {
          const pieces = dacDetails.dacDocs.submissionPieces.filter(sp => sp.docSoumission.typeDossier?.id == docTypes[index].id);
          service.setSubmissionPiecesDtos(pieces);
        }); //TODO Split responses

        this.documents = dacInfo.dynaDocs;
        this.validationValues = this.documents.reduce(
          (acc, doc) => {
            acc[doc.id] = validationData.filter(d => d.dynaDocId === doc.id) || [];
            return acc;
          },
          {} as Record<number, DacDocsFormResponse[]>
        );

        // Map dacPrix from details (if present)
        const dacPrixRequests = (dacDetails?.dacPrix || []).map(prix => ({
          monnaieId: prix.monnaie.id,
          prix: prix.prix,
        }));
        this.dacRepository.set('dacPrix', dacPrixRequests);

        if (this.id && this.version)
          this.dacService
            .generateListPdf({
              dacId: this.id,
              dacVer: this.version,
              dacDocsRequests: [],
              parametrageGeneralDacValuesRequests: [],
            })
            .subscribe({
              next: (pdfBlob: Blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  this.pdfBase64 = (reader.result as string).split(',')[1];
                  this.pdfFilename = `${this.dacDetails?.dacDocs.reference}.pdf`;
                };
                reader.readAsDataURL(pdfBlob);
                this.pdfUrl.next(URL.createObjectURL(pdfBlob));
                this.loading$.next(false);
              },
              error: () => {
                this.showError('dac.form.errors.pdfGeneration');
                this.loading$.next(false);
              },
            });

        this.dacTransformationService.initializeForm(dacInfo.dacId, this.documents, this.validationValues);

        this.formObject = getDacInspectFormObject({
          store: this.dacRepository,
          translate: (key: string, obj: any) => this.translate.instant(key, obj),
          dacId: dacInfo.dacId,
          dacVer: dacInfo.dacVer,
          pdfUrl: this.pdfUrl,
          currencyOptions: this.currencyOptions,
          nonBlacklistedCompanies: this.nonBlacklistedCompanies,
          memberOptions: this.cpmpMembers,
          multiFormAccordionsForSubmissionPieces: this.multiFormAccordionsForSubmissionPieces,
          docTypes: this.docTypes,
        });

        this.loading$.next(false);
      },
      error: () => {
        this.showError('dac.form.errors.loadDocuments');
        this.loading$.next(false);
      },
    });
  }

  private downloadSignedAAOModel(dacReference: string, alfrescoId: string) {
    this.fileService.download(
      {
        filename: `${dacReference}_modele_aao.pdf`,
        extension: 'pdf',
        alfresco_id: alfrescoId,
      },
      NAMESPACE_ENUM.DAC_MODEL
    );
  }

  private downloadSignedDACModel(dacReference: string, alfrescoId: string) {
    this.fileService.download(
      {
        filename: `${dacReference}-DAC.pdf`,
        extension: 'pdf',
        alfresco_id: alfrescoId,
      },
      NAMESPACE_ENUM.DAC_MODEL
    );
  }

  private showError(key: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.error'),
      detail: this.translate.instant(key),
      life: 5000,
    });
  }
}
